import React, { useEffect, useState } from "react";
import { func } from "prop-types";

import { locations } from "../../propTypes";
import LocationSearch from "../LocationSearch";
import SelectedLocations from "../SelectedLocations";
import * as S from "./styled";

/* This code supports a legacy feature where selecting certain areas "auto-selects"
   related areas. Selected Chelsea (id 115) or West Chelsea (id 116) will automatically
   auto-select Hudson Yards (id 146). Selecting the East Village (id 117)
   will automatically auto-select Stuyvesant Town/PCV (106) */
const autoSelectAreas = [
  { parentId: 115, childId: 146 },
  { parentId: 163, childId: 146 },
  { parentId: 117, childId: 106 }
];

const withAutoSelectAreas = (areas, areaOptions) => {
  const areaIds = areas.map(({ id }) => id);
  const autoSelected = autoSelectAreas
    .filter(
      ({ childId, parentId }) =>
        areaIds.includes(parentId) && !areaIds.includes(childId)
    )
    .map(({ childId }) => areaOptions.find(({ id }) => childId === id));

  return [...areas, ...autoSelected];
};

const LocationSelector = React.forwardRef(
  ({ areaOptions, initialSelectedAreas, onSelectedAreasChange }, ref) => {
    const [selectedAreas, setSelectedAreas] = useState(initialSelectedAreas);
    const [areaSelectedAnnouncement, setAreaSelectedAnnouncement] = useState(
      ""
    );
    const selectedAreasBlockTitle = "You Selected";

    const updateSelectedAreas = newSelectedAreas => {
      onSelectedAreasChange(newSelectedAreas);
      setSelectedAreas(newSelectedAreas);
    };

    const areaSelectedAnnouncementFor = areaName =>
      `${areaName} has been added to your list of selected areas. You can edit your list of selected areas in the ${selectedAreasBlockTitle} section.`;

    const handleAreaSelection = area => {
      const newAreaIds = [
        ...selectedAreas,
        ...withAutoSelectAreas([area], areaOptions)
      ].map(({ id }) => id);
      const uniqAreaIds = Array.from(new Set(newAreaIds));
      const newSelectedAreas = uniqAreaIds.map(id =>
        areaOptions.find(a => a.id === id)
      );

      setAreaSelectedAnnouncement(areaSelectedAnnouncementFor(area.name));
      updateSelectedAreas(newSelectedAreas);
    };

    const handleFilterClick = area => {
      const areaToRemove = selectedAreas.find(
        selectedArea => selectedArea.id === area.id
      );

      const indexToRemove = selectedAreas.indexOf(areaToRemove);

      const newSelectedAreas = [
        ...selectedAreas.slice(0, indexToRemove),
        ...selectedAreas.slice(indexToRemove + 1)
      ];

      updateSelectedAreas(newSelectedAreas);
    };

    useEffect(() => {
      if (initialSelectedAreas !== selectedAreas) {
        updateSelectedAreas(initialSelectedAreas);
      }
    }, [initialSelectedAreas]);

    return (
      <>
        <S.LocationSearchWrapper>
          <S.AriaLiveText>{areaSelectedAnnouncement}</S.AriaLiveText>
          <LocationSearch
            ref={ref}
            groupIdentity={({ group }) => group}
            id="location-search"
            labelIdentity={({ name }) => name}
            onChange={handleAreaSelection}
            options={areaOptions}
          />
        </S.LocationSearchWrapper>
        {selectedAreas.length > 0 && (
          <S.SelectedLocationsWrapper>
            <SelectedLocations
              filters={selectedAreas}
              labelIdentity={({ name }) => name}
              onFilterClick={handleFilterClick}
              title={selectedAreasBlockTitle}
            />
          </S.SelectedLocationsWrapper>
        )}
      </>
    );
  }
);

LocationSelector.propTypes = {
  areaOptions: locations.isRequired,
  initialSelectedAreas: locations,
  onSelectedAreasChange: func
};

LocationSelector.defaultProps = {
  initialSelectedAreas: [],
  onSelectedAreasChange: () => {}
};

export default LocationSelector;
